import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import integrationsService from '../../services/integrationsService';
import hotelService from '../../services/hotelService';
import SearchSelect from '../shared/SearchSelect';
import ConfirmModal from '../shared/ConfirmModal';
import BulkResyncModal from './BulkResyncModal';
import { apiErrorMessage } from '../../utils/apiError';
import '../IntegrationForm.css';                  // .ifm-impl-row
import '../shared/ImplementationWizard.css';      // .iw-empty — an implementation's unfilled count

// PIPELINES › HOTELS — the hotels that run pipelines, one row per implementation (a hotel + a pipeline template).
//
// ADDING ONE IS PICKING THE TWO: a hotel of the chain that runs no pipeline yet (a hotel runs ONE — the server refuses
// a second) and a template. The implementation is created
// at once as a draft (the server issues its Universal API credential) and opened — its board shows every credential it
// still needs in red. Nothing routes until it is switched to active there.
//
// BULK: rows can be selected (per row or all) to set STATUS or move VERSION on many at once. Status (draft/active/paused)
// applies to any selection. Version is per TEMPLATE — a v3 belongs to one template's design — so moving version is only
// offered when the selection is within ONE template; a mixed selection says so. Each is a loop over the same
// per-implementation endpoints the single page uses, so there is no new behaviour to keep in step, only a way to reach
// many at once.
//
// Scoped by the navbar: its hotel when one is chosen, otherwise its chain. A template belongs to the chain (V524), so the
// templates offered are the chain's.
export const implementationPath = (im) => `/pipelines/${im.template_id}/implementations/${im.id}`;

const STATUSES = ['draft', 'active', 'paused'];

export default function PipelineHotelsTab({ partnerId, accountId, hotelId }) {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();   // Resync is superadmin-only (server-enforced); the bulk button is hidden otherwise
  const [rows, setRows] = useState(null);        // null = not loaded
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [hotels, setHotels] = useState(null);
  const [addHotelId, setAddHotelId] = useState(null);
  const [addTemplateId, setAddTemplateId] = useState(null);
  const [adding, setAdding] = useState(false);

  // Bulk selection + actions.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkVersionId, setBulkVersionId] = useState('');
  const [versions, setVersions] = useState({ versions: [], liveVersionId: null });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [resyncRows, setResyncRows] = useState(null);   // the selection passed to the bulk resync modal (null = closed)

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await integrationsService.listHotelImplementations(
        { partner_id: partnerId, account_id: accountId, hotel_id: hotelId || undefined });
      setRows(res.implementations || []);
    } catch (e) {
      setError(apiErrorMessage(e, 'The hotels running pipelines could not be loaded.'));
      setRows([]);
    }
  }, [partnerId, accountId, hotelId]);
  useEffect(() => { load(); }, [load]);

  // What can be added: the chain's hotels, and the chain's templates.
  useEffect(() => {
    let cancelled = false;
    setTemplates(null); setHotels(null);
    integrationsService.getAllIntegrations({ partner_id: partnerId, account_id: accountId })
      // A disabled template (V693) takes no new hotel — the server refuses it too.
      .then(res => { if (!cancelled) setTemplates((res.integrations || []).filter(t => !t.mcp_source_system_id && Number(t.enabled ?? 1))); })
      .catch(() => { if (!cancelled) setTemplates([]); });
    hotelService.getAllHotels({ accountid: accountId })
      .then(data => {
        if (cancelled) return;
        setHotels((data.hotels || data || []).map(h => ({ id: h.hotelid, label: h.hotelname, code: h.hotelcode || undefined })));
      })
      .catch(() => { if (!cancelled) setHotels([]); });
    return () => { cancelled = true; };
  }, [partnerId, accountId]);
  // ONE PIPELINE PER HOTEL: only hotels not in the list are offered (and only the navbar's, when one is chosen). A row on
  // a DISABLED template does not count (V693) — that hotel is free to join another.
  const hotelItems = useMemo(() => {
    const taken = new Set((rows || []).filter(r => Number(r.template_enabled ?? 1)).map(r => String(r.hotel_id)));
    return (hotels || []).filter(h => !taken.has(String(h.id)) && (!hotelId || String(h.id) === String(hotelId)));
  }, [hotels, rows, hotelId]);
  useEffect(() => {
    if (addHotelId && !hotelItems.some(h => String(h.id) === String(addHotelId))) setAddHotelId(null);
  }, [hotelItems, addHotelId]);
  const templateItems = useMemo(() => (templates || []).map(t => ({ id: t.id, label: t.name })), [templates]);

  // A selection the current rows still contain (rows reload drops ids that are gone).
  const selectedRows = useMemo(() => (rows || []).filter(r => selected.has(r.id)), [rows, selected]);
  // Version is per template, so it can only be moved when every selected row is of ONE template.
  const sameTemplate = useMemo(() => {
    if (!selectedRows.length) return null;
    const ids = new Set(selectedRows.map(r => String(r.template_id)));
    return ids.size === 1 ? { id: selectedRows[0].template_id, name: selectedRows[0].template_name } : null;
  }, [selectedRows]);
  const sameTemplateId = sameTemplate ? sameTemplate.id : null;
  // Only pipelines whose source can be resynced (listHotelImplementations.resyncable) can be bulk-resynced.
  const resyncableSelected = useMemo(() => selectedRows.filter(r => r.resyncable), [selectedRows]);

  // The published versions of the one selected template (for the Move to version picker). Cleared for a mixed selection.
  useEffect(() => {
    if (!sameTemplateId) { setVersions({ versions: [], liveVersionId: null }); setBulkVersionId(''); return; }
    let cancelled = false;
    integrationsService.listVersions(sameTemplateId)
      .then(v => { if (!cancelled) setVersions(v || { versions: [], liveVersionId: null }); })
      .catch(() => { if (!cancelled) setVersions({ versions: [], liveVersionId: null }); });
    return () => { cancelled = true; };
  }, [sameTemplateId]);

  const add = async () => {
    const hotel = (hotels || []).find(h => String(h.id) === String(addHotelId));
    if (!hotel || !addTemplateId) return;
    setAdding(true);
    setError(null);
    try {
      const res = await integrationsService.saveImplementation(addTemplateId, {
        hotelId: hotel.id, name: hotel.label,
        // NOT ACTIVE ON CREATION: it routes nothing until its credentials are set and somebody switches it on.
        status: 'draft', bindings: [],
      });
      const created = res && res.implementation;
      if (created && created.id) navigate(implementationPath({ template_id: addTemplateId, id: created.id }));
      else { setAddTemplateId(null); load(); }
    } catch (e) {
      setError(apiErrorMessage(e, 'That hotel could not be added to the pipeline.'));
    } finally {
      setAdding(false);
    }
  };

  // ── selection ──────────────────────────────────────────────────────────────
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = (rows || []).length > 0 && rows.every(r => selected.has(r.id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set((rows || []).map(r => r.id)));
  const clearSelection = () => setSelected(new Set());
  // The header checkbox shows a dash when only some rows are selected.
  const allRef = useRef(null);
  useEffect(() => { if (allRef.current) allRef.current.indeterminate = someSelected; }, [someSelected]);

  // ── bulk apply ───────────────────────────────────────────────────────────────
  const noun = (n) => `${n} ${n === 1 ? 'pipeline' : 'pipelines'}`;
  const runBulk = async (kind) => {
    setConfirm(null);
    setBulkBusy(true);
    setError(null);
    const targets = selectedRows;
    const results = await Promise.allSettled(targets.map(im => (kind === 'status'
      ? integrationsService.setImplementationStatus(im.template_id, im.id, bulkStatus)
      : integrationsService.setImplementationVersion(im.template_id, im.id, bulkVersionId))));
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed) {
      setError(`${failed} of ${targets.length} could not be updated`
        + (kind === 'status' && bulkStatus === 'active' ? ' — active needs every credential slot filled.' : '.'));
    }
    setBulkBusy(false);
    setSelected(new Set());
    setBulkStatus('');
    setBulkVersionId('');
    await load();
  };

  const applyStatus = () => {
    if (!bulkStatus) return;
    setConfirm({
      title: `Set ${noun(selected.size)} to ${bulkStatus}`,
      message: bulkStatus === 'active'
        ? `Switch the selected ${noun(selected.size)} to active. Any with empty credential slots are refused and left as they are.`
        : `Set the selected ${noun(selected.size)} to ${bulkStatus} — they stop routing until made active again.`,
      confirmLabel: `Set ${bulkStatus}`,
      danger: bulkStatus !== 'active',
      onConfirm: () => runBulk('status'),
    });
  };
  const applyVersion = () => {
    const v = (versions.versions || []).find(x => String(x.id) === String(bulkVersionId));
    if (!v || !sameTemplate) return;
    setConfirm({
      title: `Move ${noun(selected.size)} to v${v.version}`,
      message: `The selected ${noun(selected.size)} route with v${v.version} of ${sameTemplate.name} from their next message. `
        + 'No other hotel changes.',
      confirmLabel: `Move to v${v.version}`,
      onConfirm: () => runBulk('version'),
    });
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{ flex: '1 1 240px' }}><SearchSelect
          items={hotelItems}
          value={addHotelId}
          onChange={setAddHotelId}
          disabled={adding || hotels === null}
          placeholder={hotels === null ? 'Loading hotels…' : 'Choose a hotel…'}
          searchPlaceholder="Search hotels…"
          emptyLabel="Every hotel here already runs a pipeline — a hotel runs one."
        /></div>
        <div style={{ flex: '1 1 240px' }}><SearchSelect
          items={templateItems}
          value={addTemplateId}
          onChange={setAddTemplateId}
          disabled={adding || templates === null || !addHotelId}
          placeholder={templates === null ? 'Loading templates…' : 'Choose a pipeline template…'}
          searchPlaceholder="Search templates…"
          emptyLabel="The chain has no pipeline templates yet — create one on the Pipeline Templates tab."
        /></div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={add}
          disabled={!addHotelId || !addTemplateId || adding}
          title="Run this template at this hotel — then set each red credential on its board"
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          <Plus size={16} /> {adding ? 'Adding…' : 'Add'}
        </button>
      </div>

      {error && <div className="os-msg os-msg-error">{error}</div>}

      {/* BULK BAR — only while rows are selected. Status applies to any selection; version only to one template. */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', background: '#f1f5f9',
          border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.6rem 0.9rem' }}>
          <strong style={{ fontSize: '0.9rem' }}>{selected.size} selected</strong>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <label htmlFor="bulk-status" style={{ fontSize: '0.85rem', color: '#374151' }}>Status</label>
            <select id="bulk-status" value={bulkStatus} disabled={bulkBusy}
              onChange={(e) => setBulkStatus(e.target.value)} style={{ padding: '0.25rem 0.4rem' }}>
              <option value="">Choose…</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="button" className="btn btn-secondary" disabled={!bulkStatus || bulkBusy}
              onClick={applyStatus} style={{ whiteSpace: 'nowrap' }}>Apply</button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <label htmlFor="bulk-version" style={{ fontSize: '0.85rem', color: '#374151' }}>Version</label>
            {sameTemplate ? (
              (versions.versions || []).length ? (
                <>
                  <select id="bulk-version" value={bulkVersionId} disabled={bulkBusy}
                    onChange={(e) => setBulkVersionId(e.target.value)} style={{ padding: '0.25rem 0.4rem' }}>
                    <option value="">Choose…</option>
                    {versions.versions.map(v => (
                      <option key={v.id} value={v.id}>
                        v{v.version}{String(v.id) === String(versions.liveVersionId) ? ' (live)' : ''}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-secondary" disabled={!bulkVersionId || bulkBusy}
                    onClick={applyVersion} style={{ whiteSpace: 'nowrap' }}>Apply</button>
                </>
              ) : (
                <span className="form-help" style={{ margin: 0 }}>{sameTemplate.name} has no published version yet.</span>
              )
            ) : (
              <span className="form-help" style={{ margin: 0 }}>Select hotels of one template to move version.</span>
            )}
          </div>

          {isSuperAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <button type="button" className="btn btn-secondary" disabled={bulkBusy || resyncableSelected.length === 0}
                onClick={() => setResyncRows(resyncableSelected)}
                title={resyncableSelected.length === 0 ? 'None of the selected pipelines has a resyncable source' : 'Resync the selected pipelines with one set of criteria'}
                style={{ whiteSpace: 'nowrap' }}>
                Resync{resyncableSelected.length ? ` (${resyncableSelected.length})` : ''}
              </button>
              {resyncableSelected.length > 0 && resyncableSelected.length < selected.size && (
                <span className="form-help" style={{ margin: 0 }}>{selected.size - resyncableSelected.length} not resyncable</span>
              )}
            </div>
          )}

          <button type="button" className="btn btn-link" onClick={clearSelection} disabled={bulkBusy}
            style={{ marginLeft: 'auto' }}>Clear</button>
        </div>
      )}

      {rows === null ? (
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading hotels…</p>
      ) : rows.length === 0 ? (
        <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.5rem', color: '#6b7280', fontSize: '0.925rem' }}>
          {hotelId ? 'This hotel runs no pipelines yet.' : 'No hotel in this chain runs a pipeline yet.'} Choose a hotel and a
          pipeline template and <strong>Add</strong> it.
        </div>
      ) : (
        <table className="acp-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input ref={allRef} type="checkbox" checked={allSelected} onChange={toggleAll}
                  aria-label="Select all pipelines" />
              </th>
              <th>Hotel</th><th>Pipeline template</th><th>Version</th><th>Status</th><th>Filled</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(im => (
              <tr
                key={im.id}
                className="ifm-impl-row"
                onClick={() => navigate(implementationPath(im))}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(implementationPath(im)); } }}
                tabIndex={0}
                role="button"
                title={`Open ${im.hotelname || im.name} — ${im.template_name}`}
              >
                {/* The checkbox cell does not open the row. */}
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(im.id)} onChange={() => toggle(im.id)}
                    aria-label={`Select ${im.hotelname || im.name}`} />
                </td>
                <td>
                  <div style={{ fontWeight: 600 }}>{im.hotelname || <em>no property</em>}</div>
                  {im.chain_name && <div className="form-help" style={{ margin: 0 }}>{im.chain_name}</div>}
                </td>
                <td>
                  {im.template_name}
                  {!Number(im.template_enabled ?? 1) && <div className="form-help" style={{ margin: 0 }}>template disabled</div>}
                </td>
                <td>
                  {im.version ? `v${im.version}` : <span className="form-help" style={{ margin: 0 }}>draft</span>}
                  {im.version && im.live_version && im.version !== im.live_version && (
                    <div className="form-help" style={{ margin: 0 }}>v{im.live_version} is live</div>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${im.status === 'active' ? 'status-active' : 'status-pending'}`}>{im.status}</span>
                </td>
                <td className={im.filled < im.slots ? 'iw-empty' : ''}>{im.filled} of {im.slots}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {resyncRows && (
        <BulkResyncModal
          rows={resyncRows}
          onClose={() => { setResyncRows(null); clearSelection(); }}
        />
      )}
    </div>
  );
}
